import { Skeleton } from "@solomon-labs/design/components/ui";
import React, { useEffect, useRef, useState } from "react";

const REVEAL_MS = 300;

type SkeletonSurface = "light" | "dark";
type RevealState = "loading" | "animating" | "done";

interface SkeletonRevealProps {
    /** Loading flag — flips from true → false to trigger the reveal. */
    isLoading: boolean;
    /**
     * CSS width of the wrapper during the loading state. This is the width
     * the animation transitions *from*. Typically matches the skeleton's
     * own width class (e.g. `"2em"` alongside `w-[2em]`).
     */
    skeletonWidth: string;
    /** Tailwind classes applied to the inner `Skeleton` (width/height/rounded/translate). */
    skeletonClassName?: string;
    /** Passed through to `Skeleton`. */
    skeletonSurface?: SkeletonSurface;
    /** Real content revealed once loading completes. */
    children: React.ReactNode;
}

/**
 * @version 1.0.0
 * @published 2026-04-17
 * @history
 *   1.0.0 — Initial publish. Inline-block wrapper that measures its
 *           content width on load completion, transitions the wrapper
 *           width from `skeletonWidth` → measured px, and cross-fades
 *           the skeleton out / content in with a blur. Uses CSS
 *           transitions (compositor thread) — no motion/react.
 */
export function SkeletonReveal({
    isLoading,
    skeletonWidth,
    skeletonClassName = "",
    skeletonSurface = "light",
    children
}: SkeletonRevealProps): React.ReactElement {
    const contentRef = useRef<HTMLSpanElement>(null);
    const [state, setState] = useState<RevealState>(
        isLoading ? "loading" : "done"
    );
    const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);

    useEffect(() => {
        if (isLoading || state !== "loading" || !contentRef.current) return;
        setMeasuredWidth(contentRef.current.offsetWidth);
        const raf = requestAnimationFrame(() => setState("animating"));
        return (): void => cancelAnimationFrame(raf);
    }, [isLoading, state]);

    useEffect(() => {
        if (state !== "animating") return;
        const id = setTimeout(() => setState("done"), REVEAL_MS + 50);
        return (): void => clearTimeout(id);
    }, [state]);

    const widthStyle =
        state === "loading"
            ? skeletonWidth
            : state === "animating" &&
                measuredWidth !== null &&
                measuredWidth !== undefined
              ? `${measuredWidth}px`
              : undefined;

    const isRevealed = state !== "loading";

    return (
        <span
            className="relative inline-block align-baseline transition-[width] duration-300 ease-[cubic-bezier(0.215, 0.61, 0.355, 1)]"
            style={{ width: widthStyle }}
        >
            <span
                ref={contentRef}
                className={`inline-block whitespace-nowrap transition-[opacity,filter] duration-300 ease-[cubic-bezier(0.215, 0.61, 0.355, 1)] ${
                    isRevealed ? "opacity-100 blur-0" : "opacity-0 blur-[3px]"
                }`}
                aria-hidden={!isRevealed}
            >
                {children || "\u00A0"}
            </span>
            {/* Skeleton stays in the tree for `loading` (visible,
                pulsing) and `animating` (cross-fading out), then
                unmounts entirely on `done`. The unmount is necessary
                because `Skeleton` ships `animate-pulse`, whose
                opacity keyframes override the static `opacity-0` we
                add when fading out — so the element keeps pulsing
                between full and half opacity forever. Removing it
                from the DOM stops the animation from playing. */}
            {state !== "done" && (
                <Skeleton
                    inline
                    surface={skeletonSurface}
                    className={`absolute left-0 top-1/2 -translate-y-1/2 transition-[opacity,filter] duration-300 ease-[cubic-bezier(0.215, 0.61, 0.355, 1)] ${skeletonClassName} ${
                        isRevealed
                            ? "opacity-0 blur-[3px] pointer-events-none"
                            : "opacity-100 blur-0"
                    }`}
                />
            )}
        </span>
    );
}
